import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  FileText,
  Plus,
  Search,
  RefreshCw,
  Loader2,
  Upload,
  Truck,
  FileCheck,
  Shield,
  Ship,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  Trash2,
  Eye,
  Mail,
  Calendar,
  Hash,
  FolderOpen,
  Zap,
  ArrowUpRight,
  Link2,
  Send,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";


// Category configuration
const CATEGORY_CONFIG = {
  t1: { icon: Truck, color: "bg-purple-500", textColor: "text-purple-600", label: "T1 Transit" },
  customs: { icon: Shield, color: "bg-blue-500", textColor: "text-blue-600", label: "Customs Clearance" },
  tcc: { icon: Ship, color: "bg-green-500", textColor: "text-green-600", label: "TCC" },
  cmr: { icon: FileText, color: "bg-orange-500", textColor: "text-orange-600", label: "CMR" },
  export: { icon: ArrowUpRight, color: "bg-teal-500", textColor: "text-teal-600", label: "Export" },
};

export const TrinetPage = () => {
  const { getAuthHeaders, showStaffFeatures } = useAuth();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, by_category: {} });
  const [loading, setLoading] = useState(true);
  const [pendingEmails, setPendingEmails] = useState([]);
  const [pendingLinkEvents, setPendingLinkEvents] = useState([]);
  const [activeTab, setActiveTab] = useState("events");
  const [eventTypes, setEventTypes] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showParseResult, setShowParseResult] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [eventToLink, setEventToLink] = useState(null);
  const [linkPfNumber, setLinkPfNumber] = useState("");
  const [sendNotification, setSendNotification] = useState(false);
  const [linking, setLinking] = useState(false);
  const [parsedEmail, setParsedEmail] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const dropZoneRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const [formData, setFormData] = useState({
    pf_number: "",
    event_type: "",
    mrn: "",
    reference_number: "",
    notes: "",
  });

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ per_page: "100" });
      if (selectedCategory !== "all") {
        params.append("category", selectedCategory);
      }
      
      const response = await axios.get(`${API_URL}/trinet/events?${params}`, {
        headers: getAuthHeaders(),
      });
      setEvents(response.data.events || []);
    } catch (error) {
      console.error("Failed to fetch events:", error);
      toast.error("Failed to load Trinet events");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selectedCategory]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/trinet/stats`, {
        headers: getAuthHeaders(),
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, [getAuthHeaders]);

  const fetchEventTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/trinet/event-types`, {
        headers: getAuthHeaders(),
      });
      setEventTypes(response.data.event_types || {});
    } catch (error) {
      console.error("Failed to fetch event types:", error);
    }
  }, [getAuthHeaders]);

  const fetchPendingEmails = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/trinet/pending`, {
        headers: getAuthHeaders(),
      });
      setPendingEmails(response.data.pending || []);
    } catch (error) {
      console.error("Failed to fetch pending emails:", error);
    }
  }, [getAuthHeaders]);

  const fetchPendingLinkEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/trinet/events/pending-link`, {
        headers: getAuthHeaders(),
      });
      setPendingLinkEvents(response.data.events || []);
    } catch (error) {
      console.error("Failed to fetch pending link events:", error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchEvents();
    fetchStats();
    fetchEventTypes();
    fetchPendingEmails();
    fetchPendingLinkEvents();
  }, [fetchEvents, fetchStats, fetchEventTypes, fetchPendingEmails, fetchPendingLinkEvents]);

  const handleLinkToPF = async () => {
    if (!eventToLink || !linkPfNumber) {
      toast.error("Please enter a PF number");
      return;
    }

    try {
      setLinking(true);
      await axios.post(
        `${API_URL}/trinet/events/${eventToLink.id}/link-pf`,
        {
          pf_number: linkPfNumber,
          send_customer_notification: sendNotification,
        },
        { headers: getAuthHeaders() }
      );

      toast.success(`Event linked to ${linkPfNumber} successfully`);
      setShowLinkModal(false);
      setEventToLink(null);
      setLinkPfNumber("");
      setSendNotification(false);
      
      // Refresh data
      fetchEvents();
      fetchPendingLinkEvents();
      fetchStats();
    } catch (error) {
      console.error("Failed to link event:", error);
      toast.error(error.response?.data?.detail || "Failed to link event to PF");
    } finally {
      setLinking(false);
    }
  };

  const openLinkModal = (event) => {
    setEventToLink(event);
    setLinkPfNumber(event.pf_number || "");
    setSendNotification(false);
    setShowLinkModal(true);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    try {
      setParsing(true);
      const response = await axios.post(`${API_URL}/trinet/parse-email`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      
      setParsedEmail(response.data);
      setShowParseResult(true);
      
      // Pre-fill form
      setFormData({
        pf_number: response.data.pf_number || "",
        event_type: response.data.event_type || "",
        mrn: response.data.mrn || "",
        reference_number: response.data.container || response.data.cmr || "",
        notes: "",
      });
      
      if (response.data.pf_number && response.data.event_type) {
        toast.success(`Detected: ${response.data.event_type} for ${response.data.pf_number}`);
      } else {
        toast.warning("Could not fully parse email. Please fill in missing fields.");
      }
    } catch (error) {
      console.error("Parse error:", error);
      toast.error(error.response?.data?.detail || "Failed to parse email");
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.msg') && !file.name.toLowerCase().endsWith('.eml')) {
      toast.error("Please drop a .msg or .eml email file");
      return;
    }
    
    await handleFileUpload(file);
  };

  const handleCreateEvent = async () => {
    if (!formData.pf_number || !formData.event_type) {
      toast.error("PF number and event type are required");
      return;
    }
    
    try {
      setCreating(true);
      
      const payload = {
        ...formData,
        attachments: parsedEmail?.attachments || [],
        source_email_subject: parsedEmail?.subject || null,
      };
      
      await axios.post(`${API_URL}/trinet/events`, payload, {
        headers: getAuthHeaders(),
      });
      
      toast.success("Trinet event created successfully");
      setShowParseResult(false);
      setShowCreateModal(false);
      setParsedEmail(null);
      setFormData({ pf_number: "", event_type: "", mrn: "", reference_number: "", notes: "" });
      fetchEvents();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create event");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    
    try {
      await axios.delete(`${API_URL}/trinet/events/${eventId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Event deleted");
      fetchEvents();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const handleDismissPending = async (pendingId) => {
    if (!window.confirm("Dismiss this pending email?")) return;
    
    try {
      await axios.delete(`${API_URL}/trinet/pending/${pendingId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Pending email dismissed");
      fetchPendingEmails();
    } catch (error) {
      toast.error("Failed to dismiss");
    }
  };

  const handleProcessPending = (pending) => {
    // Pre-fill form with pending data
    setFormData({
      pf_number: pending.parsed_pf || "",
      event_type: pending.parsed_event_type || "",
      mrn: pending.parsed_mrn || "",
      reference_number: "",
      notes: `From: ${pending.sender}\nSubject: ${pending.subject}`,
    });
    setParsedEmail({
      subject: pending.subject,
      sender: pending.sender,
      body: pending.body,
      attachments: [],
    });
    setShowCreateModal(true);
  };

  const filteredEvents = events.filter(event => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      event.pf_number?.toLowerCase().includes(query) ||
      event.mrn?.toLowerCase().includes(query) ||
      event.reference_number?.toLowerCase().includes(query) ||
      event.event_label?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("sl-SI", { 
      day: "2-digit", 
      month: "2-digit", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (!showStaffFeatures) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto p-6 pt-24 text-center">
          <Shield className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold mb-2">Access Restricted</h1>
          <p className="text-muted-foreground">Trinet module is only available for staff.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="container mx-auto p-6 pt-24 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg">
                <Zap className="h-6 w-6 text-white" />
              </div>
              Trinet Module
            </h1>
            <p className="text-muted-foreground mt-1">
              Customs workflow email processing
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { fetchEvents(); fetchStats(); fetchPendingEmails(); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Event
            </Button>
          </div>
        </div>

        {/* Info Banner - Email Auto-Processing */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <p className="font-medium text-blue-700">Automatic Email Processing</p>
              <p className="text-sm text-blue-600">
                Emails from <code className="bg-blue-100 px-1 rounded">tlm.obvescanje@europacific.com</code> to <code className="bg-blue-100 px-1 rounded">roket@europacific.com</code> are automatically processed.
              </p>
            </div>
          </div>
        </div>

        {/* TRINET WEB SYNC PANEL — cookie-based scraper for T02/T21 statuses */}
        <TrinetWebSyncCard />

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Events</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <FileText className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Today</p>
                  <p className="text-2xl font-bold text-green-600">{stats.today}</p>
                </div>
                <Clock className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          {pendingEmails.length > 0 && (
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 cursor-pointer" onClick={() => setActiveTab("pending")}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending</p>
                    <p className="text-2xl font-bold text-amber-600">{pendingEmails.length}</p>
                  </div>
                  <AlertCircle className="h-8 w-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
          )}
          
          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
            <Card key={key} className={`bg-gradient-to-br from-white to-slate-50`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{config.label}</p>
                    <p className={`text-2xl font-bold ${config.textColor}`}>
                      {stats.by_category?.[key] || 0}
                    </p>
                  </div>
                  <config.icon className={`h-8 w-8 ${config.textColor} opacity-50`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs for Events and Pending */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="events">Events ({events.length})</TabsTrigger>
            <TabsTrigger value="pending-link" className="relative">
              Needs PF Link
              {pendingLinkEvents.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full">
                  {pendingLinkEvents.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="pending" className="relative">
              Pending Review
              {pendingEmails.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                  {pendingEmails.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="mt-4">
            {/* Drag & Drop Zone */}
            <div
              ref={dropZoneRef}
          className={`mb-6 border-2 border-dashed rounded-xl p-8 text-center transition-all ${
            isDragging 
              ? "border-primary bg-primary/5" 
              : "border-muted-foreground/30 hover:border-primary/50"
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={(e) => {
            if (e.currentTarget === dropZoneRef.current && !e.currentTarget.contains(e.relatedTarget)) {
              setIsDragging(false);
            }
          }}
          onDrop={handleDrop}
          data-testid="trinet-drop-zone"
        >
          {parsing ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="text-muted-foreground">Parsing email...</p>
            </div>
          ) : (
            <>
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium">Drag & drop email file here</p>
              <p className="text-sm text-muted-foreground">.msg or .eml files from your customs system</p>
              <input
                type="file"
                accept=".msg,.eml"
                className="hidden"
                id="trinet-upload"
                onChange={(e) => handleFileUpload(e.target.files?.[0])}
              />
              <Label htmlFor="trinet-upload" className="mt-3 inline-block">
                <Button variant="outline" size="sm" asChild>
                  <span><Mail className="h-4 w-4 mr-2" />Browse Files</span>
                </Button>
              </Label>
            </>
          )}
        </div>

        {/* Category Filter & Search */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory("all")}
            >
              All
            </Button>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
              <Button
                key={key}
                variant={selectedCategory === key ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(key)}
                className={selectedCategory === key ? config.color : ""}
              >
                <config.icon className="h-4 w-4 mr-1" />
                {config.label}
              </Button>
            ))}
          </div>
          
          <div className="flex-1 max-w-xs">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search PF, MRN, container..."
                className="pl-10"
              />
            </div>
          </div>
        </div>

        {/* Events List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Events ({filteredEvents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No Trinet events found</p>
                <p className="text-sm">Drag & drop an email to create one</p>
              </div>
            ) : (
              <div className="space-y-3">
                <AnimatePresence>
                  {filteredEvents.map((event) => {
                    const catConfig = CATEGORY_CONFIG[event.event_category] || CATEGORY_CONFIG.customs;
                    const CatIcon = catConfig.icon;
                    
                    return (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className={`p-2 rounded-lg ${catConfig.color}`}>
                          <CatIcon className="h-5 w-5 text-white" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-sm font-medium ${catConfig.textColor}`}>
                              {event.event_label}
                            </span>
                            <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">
                              {event.event_category.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1 font-medium">
                              <FolderOpen className="h-3 w-3" />
                              {event.pf_number}
                            </span>
                            {event.mrn && (
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <Hash className="h-3 w-3" />
                                MRN: {event.mrn}
                              </span>
                            )}
                            {event.reference_number && (
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <Package className="h-3 w-3" />
                                {event.reference_number}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          {formatDate(event.created_at)}
                        </div>
                        
                        <div className="flex items-center gap-1">
                          {event.attachments?.length > 0 && (
                            <Button variant="ghost" size="sm" onClick={() => setSelectedEvent(event)}>
                              <Download className="h-4 w-4" />
                              <span className="ml-1">{event.attachments.length}</span>
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(event.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Pending Link Events Tab */}
          <TabsContent value="pending-link" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-purple-500" />
                  Events Needing PF Link ({pendingLinkEvents.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingLinkEvents.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No events pending link</p>
                    <p className="text-sm">All events have been linked to Position Folders</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingLinkEvents.map((event) => {
                      const categoryConfig = CATEGORY_CONFIG[event.event_category] || {};
                      const CategoryIcon = categoryConfig.icon || FileText;
                      
                      return (
                        <div
                          key={event.id}
                          className="flex items-start gap-4 p-4 bg-purple-50 border border-purple-200 rounded-lg"
                          data-testid={`pending-link-event-${event.id}`}
                        >
                          <div className={`p-2 rounded-lg ${categoryConfig.color || 'bg-gray-500'}`}>
                            <CategoryIcon className="h-5 w-5 text-white" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground mb-1">
                              {event.event_label}
                            </p>
                            <p className="text-sm text-muted-foreground truncate mb-2">
                              {event.source_email_subject}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                              {event.mrn && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                  MRN: {event.mrn}
                                </span>
                              )}
                              {event.truck_registration && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                  Truck: {event.truck_registration}
                                </span>
                              )}
                              {event.cmr && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">
                                  CMR: {event.cmr}
                                </span>
                              )}
                              {event.attachments?.length > 0 && (
                                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded flex items-center gap-1">
                                  <FileText className="h-3 w-3" />
                                  {event.attachments.length} PDF
                                </span>
                              )}
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                {formatDate(event.created_at)}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              onClick={() => openLinkModal(event)}
                              data-testid={`link-pf-btn-${event.id}`}
                            >
                              <Link2 className="h-4 w-4 mr-1" />
                              Link to PF
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pending" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  Pending Emails ({pendingEmails.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingEmails.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No pending emails</p>
                    <p className="text-sm">All Trinet emails have been processed</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingEmails.map((pending) => (
                      <div
                        key={pending.id}
                        className="flex items-start gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg"
                      >
                        <div className="p-2 rounded-lg bg-amber-100">
                          <Mail className="h-5 w-5 text-amber-600" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate mb-1">
                            {pending.subject}
                          </p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                            <span>From: {pending.sender}</span>
                            <span>{formatDate(pending.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            {pending.parsed_pf && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                PF: {pending.parsed_pf}
                              </span>
                            )}
                            {pending.parsed_mrn && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                MRN: {pending.parsed_mrn}
                              </span>
                            )}
                            {pending.parsed_event_type && (
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                {pending.parsed_event_type}
                              </span>
                            )}
                            {!pending.parsed_pf && (
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                                Missing PF Number
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button size="sm" onClick={() => handleProcessPending(pending)}>
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Process
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDismissPending(pending.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Event Modal */}
      <Dialog open={showCreateModal || showParseResult} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setShowParseResult(false);
          setParsedEmail(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              {showParseResult ? "Confirm Trinet Event" : "Create Trinet Event"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {showParseResult && parsedEmail && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
                <p className="font-medium text-blue-700 mb-1">Parsed from email:</p>
                <p className="text-blue-600 truncate">{parsedEmail.subject}</p>
                {parsedEmail.attachments?.length > 0 && (
                  <p className="text-blue-500 text-xs mt-1">
                    {parsedEmail.attachments.length} attachment(s) will be saved
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>PF Number *</Label>
                <Input
                  value={formData.pf_number}
                  onChange={(e) => setFormData({ ...formData, pf_number: e.target.value })}
                  placeholder="PF-000001-26"
                  data-testid="trinet-pf-input"
                />
              </div>
              <div>
                <Label>Event Type *</Label>
                <Select value={formData.event_type} onValueChange={(v) => setFormData({ ...formData, event_type: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(eventTypes).map(([key, info]) => (
                      <SelectItem key={key} value={key}>
                        {info.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>MRN</Label>
                <Input
                  value={formData.mrn}
                  onChange={(e) => setFormData({ ...formData, mrn: e.target.value })}
                  placeholder="26SI00102640022395"
                />
              </div>
              <div>
                <Label>Reference (Container/CMR)</Label>
                <Input
                  value={formData.reference_number}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  placeholder="ABCD1234567"
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => {
                setShowCreateModal(false);
                setShowParseResult(false);
                setParsedEmail(null);
              }}>
                Cancel
              </Button>
              <Button onClick={handleCreateEvent} disabled={creating}>
                {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Create Event
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attachments Modal */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Event Attachments</DialogTitle>
          </DialogHeader>
          
          {selectedEvent?.attachments?.length > 0 ? (
            <div className="space-y-2">
              {selectedEvent.attachments.map((att, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">{att.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {att.size ? `${Math.round(att.size / 1024)} KB` : att.content_type}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <a href={att.data} download={att.filename}>
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </a>
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No attachments</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Link to PF Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-purple-500" />
              Link Event to Position Folder
            </DialogTitle>
          </DialogHeader>
          
          {eventToLink && (
            <div className="space-y-4">
              {/* Event Info */}
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="font-medium text-purple-700">{eventToLink.event_label}</p>
                <div className="mt-2 space-y-1 text-sm">
                  {eventToLink.mrn && (
                    <p className="text-gray-600">
                      <span className="font-medium">MRN:</span> {eventToLink.mrn}
                    </p>
                  )}
                  {eventToLink.truck_registration && (
                    <p className="text-gray-600">
                      <span className="font-medium">Truck:</span> {eventToLink.truck_registration}
                    </p>
                  )}
                  {eventToLink.cmr && (
                    <p className="text-gray-600">
                      <span className="font-medium">CMR:</span> {eventToLink.cmr}
                    </p>
                  )}
                </div>
              </div>

              {/* PF Number Input */}
              <div className="space-y-2">
                <Label htmlFor="link-pf-number">Position Folder Number *</Label>
                <Input
                  id="link-pf-number"
                  placeholder="PF-000000-00"
                  value={linkPfNumber}
                  onChange={(e) => setLinkPfNumber(e.target.value)}
                  data-testid="link-pf-number-input"
                />
                <p className="text-xs text-muted-foreground">
                  Enter the PF number to link this event to
                </p>
              </div>

              {/* Send Notification Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="send-notification"
                  checked={sendNotification}
                  onCheckedChange={setSendNotification}
                />
                <label
                  htmlFor="send-notification"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Send notification email to customer
                </label>
              </div>

              {/* Info about what happens */}
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p className="font-medium mb-1">When linked:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-600">
                  <li>Milestone will be added to PF tracking</li>
                  {eventToLink.event_category === "t1" && eventToLink.attachments?.length > 0 && (
                    <li>T1 PDF will be stored in PF documents</li>
                  )}
                  {sendNotification && (
                    <li>Customer will receive email notification</li>
                  )}
                </ul>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowLinkModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleLinkToPF}
                  disabled={linking || !linkPfNumber}
                  data-testid="confirm-link-btn"
                >
                  {linking ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Linking...
                    </>
                  ) : (
                    <>
                      <Link2 className="h-4 w-4 mr-2" />
                      Link to PF
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============================================================================
// Trinet Inbound Agents (Windows-side push) admin card
// Replaces the previous (mis-conceived) cookie-paste card. TLM is a Delphi
// desktop client connecting directly to a hosted MSSQL DB; there's no web
// UI or cookies to scrape. So we provide a small PowerShell agent that runs
// on the same Windows box (VM-EPHOLDING), queries MSSQL every 10 min and
// pushes deltas to /api/trinet-inbound/status. This card lets an admin
// create/revoke agent tokens.
// ============================================================================
function TrinetWebSyncCard() {
  const { getAuthHeaders } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newToken, setNewToken] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadAgents = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/trinet-inbound/agents`, { headers: getAuthHeaders() });
      setAgents(res.data?.agents || []);
    } catch {
      // not authorised — collapse
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);
  useEffect(() => { loadAgents(); }, [loadAgents]);

  const create = async () => {
    if (!newName.trim()) { toast.error("Name required"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/trinet-inbound/agents`,
        { name: newName.trim() }, { headers: getAuthHeaders() });
      setNewToken(res.data.token);
      setNewName("");
      await loadAgents();
      toast.success("Agent created — copy the token now, it won't be shown again");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const revoke = async (id, name) => {
    if (!window.confirm(`Revoke agent "${name}"? Its token will stop working immediately.`)) return;
    try {
      await axios.delete(`${API_URL}/trinet-inbound/agents/${id}`, { headers: getAuthHeaders() });
      toast.success("Agent revoked");
      await loadAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Revoke failed");
    }
  };

  if (loading) return null;

  const active = agents.filter((a) => a.active);
  const hasAgent = active.length > 0;

  return (
    <Card className="mb-6 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-base">Trinet Inbound Agents</CardTitle>
            <div className="flex items-center gap-2 text-xs">
              <span className={`inline-block w-2 h-2 rounded-full ${hasAgent ? "bg-emerald-500" : "bg-gray-400"}`}></span>
              <span className="text-muted-foreground">
                {hasAgent ? `${active.length} active agent${active.length > 1 ? "s" : ""}` : "No agent yet"}
              </span>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setShowCreate(true); setNewToken(null); }}
            data-testid="trinet-create-agent-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New agent
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <p className="text-xs text-muted-foreground">
          TLM is a Delphi desktop client (no web UI to scrape). To capture statuses TLM doesn't email
          (T02 Customs Clearance Started, T21 Documentary Control, etc.), deploy the
          {" "}<a href="/docs/trinet-sync-agent.ps1" target="_blank" rel="noreferrer"
            className="text-blue-600 underline font-medium">PowerShell agent</a>{" "}
          on the Windows machine that hosts TLM. The agent queries MSSQL every 10 min and pushes deltas here. Idempotent server-side, so safe to re-run.
        </p>

        {agents.length > 0 && (
          <div className="bg-white border rounded-lg divide-y" data-testid="trinet-agents-list">
            {agents.map((a) => (
              <div key={a.id} className="flex items-center justify-between p-3 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium flex items-center gap-2">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${a.active ? "bg-emerald-500" : "bg-rose-500"}`}></span>
                    {a.name}
                    {!a.active && <span className="text-xs text-rose-600">(revoked)</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Last seen: {a.last_seen_at ? new Date(a.last_seen_at).toLocaleString() : "never"}
                    {" · "}Requests: {a.request_count || 0}
                    {" · "}Records added: {a.records_added || 0}
                  </div>
                </div>
                {a.active && (
                  <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700"
                    onClick={() => revoke(a.id, a.name)} data-testid={`trinet-revoke-agent-${a.id}`}>
                    Revoke
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {showCreate && (
          <div className="bg-white border rounded-lg p-3 space-y-3" data-testid="trinet-create-agent-panel">
            {newToken ? (
              <>
                <div className="text-sm">
                  <div className="font-semibold text-emerald-700 mb-2">✓ Agent created. Copy this token NOW — it will not be shown again:</div>
                  <div className="font-mono text-xs bg-slate-100 p-2 rounded break-all select-all" data-testid="trinet-new-agent-token">{newToken}</div>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div className="font-semibold text-foreground">Next steps on your Windows machine (VM-EPHOLDING)</div>
                  <ol className="list-decimal pl-5 space-y-0.5">
                    <li>Open PowerShell as Administrator.</li>
                    <li>Run:
                      <pre className="bg-slate-100 p-1.5 rounded text-[10px] mt-1 whitespace-pre-wrap">{`[System.Environment]::SetEnvironmentVariable('TRINET_INBOUND_TOKEN','${newToken}','Machine')
[System.Environment]::SetEnvironmentVariable('MYEUROPACIFIC_API_URL','https://myeuropacific.com','Machine')`}</pre>
                    </li>
                    <li>Download <a href="/docs/trinet-sync-agent.ps1" target="_blank" rel="noreferrer" className="text-blue-600 underline">trinet-sync-agent.ps1</a> to <code className="bg-slate-100 px-1">C:\europacific\</code>.</li>
                    <li>Unblock the downloaded file (one-time, removes "internet zone" warning):
                      <pre className="bg-slate-100 p-1.5 rounded text-[10px] mt-1 whitespace-pre-wrap">{`Unblock-File -Path C:\\europacific\\trinet-sync-agent.ps1`}</pre>
                    </li>
                    <li>Dry-run test (reads MSSQL, prints what would be pushed, does NOT push):
                      <pre className="bg-slate-100 p-1.5 rounded text-[10px] mt-1 whitespace-pre-wrap">{`powershell -NoProfile -ExecutionPolicy Bypass -File C:\\europacific\\trinet-sync-agent.ps1 -DryRun`}</pre>
                    </li>
                    <li>Schedule:
                      <pre className="bg-slate-100 p-1.5 rounded text-[10px] mt-1 whitespace-pre-wrap">{`schtasks /Create /SC MINUTE /MO 10 /TN "EuroPacific TriNet Sync" \\
  /TR "powershell -NoProfile -ExecutionPolicy Bypass -File C:\\europacific\\trinet-sync-agent.ps1" /RU SYSTEM`}</pre>
                    </li>
                  </ol>
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={() => { setShowCreate(false); setNewToken(null); }}>Done</Button>
                </div>
              </>
            ) : (
              <>
                <Label className="text-sm">Agent name</Label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. VM-EPHOLDING SQL agent"
                  data-testid="trinet-new-agent-name"
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setShowCreate(false); setNewName(""); }}>Cancel</Button>
                  <Button size="sm" onClick={create} disabled={creating} className="bg-blue-600 hover:bg-blue-700"
                    data-testid="trinet-submit-create-agent">
                    {creating ? "Creating…" : "Create agent"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default TrinetPage;
